import { useState } from 'react';
import type { CheckResult, ConnectionChecks, ProviderStatus } from '../../shared/types';
import { PROVIDER_STATUS_LABEL, PROVIDER_STATUS_STYLE } from '../../shared/types';

/** The four-word health vocabulary, coloured per the handoff's status table. */
export function StatusPill({ status }: { status: ProviderStatus }) {
  return <span className="ad-pill" style={PROVIDER_STATUS_STYLE[status]}>{PROVIDER_STATUS_LABEL[status]}</span>;
}

/**
 * Email-domain chips. Enter or comma commits, Backspace on an empty input
 * removes the last one, duplicates are ignored, and values are lowercased with
 * a leading @ stripped — so pasting "@Ooma.com" does the obvious thing.
 */
export function DomainChips({ value, onChange, placeholder }: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const next = [...value];
    for (const part of raw.split(/[\s,;]+/)) {
      const d = part.trim().toLowerCase().replace(/^@+/, '');
      if (d && !next.includes(d)) next.push(d);
    }
    if (next.length !== value.length) onChange(next);
    setDraft('');
  };

  return (
    <div className="sso-chips">
      {value.map((d) => (
        <span className="sso-chip" key={d}>
          {d}
          <button type="button" title={`Remove ${d}`} onClick={() => onChange(value.filter((x) => x !== d))}>×</button>
        </span>
      ))}
      <input
        value={draft}
        placeholder={placeholder || 'add a domain and press Enter…'}
        onChange={(e) => {
          if (/[,;\s]$/.test(e.target.value)) commit(e.target.value);
          else setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); if (draft.trim()) commit(draft); }
          if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => { if (draft.trim()) commit(draft); }}
      />
    </div>
  );
}

/** A monospace URI with a Copy button — what a tenant sends their IdP admin. */
export function CopyUri({ uri }: { uri: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="sso-uri">
      <code>{uri}</code>
      <button
        className="fc-act"
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(uri).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }).catch(() => undefined);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** The six rows, in the order the handoff lists them. */
type CheckKey = 'discovery' | 'jwks' | 'flow' | 'credentials' | 'redirectUri' | 'liveSignIn';

const CHECK_ROWS: { key: CheckKey; title: string }[] = [
  { key: 'discovery', title: 'Discovery document reachable' },
  { key: 'jwks', title: 'Signing keys published' },
  { key: 'flow', title: 'Flow supported' },
  { key: 'credentials', title: 'Client ID and secret accepted' },
  { key: 'redirectUri', title: 'Redirect URI registered' },
  { key: 'liveSignIn', title: 'A real person has signed in' },
];

/** The six-row checklist. A row with no result yet reads as pending, not failed. */
export function ChecksList({ checks, running }: { checks: ConnectionChecks | null; running?: boolean }) {
  return (
    <div>
      {CHECK_ROWS.map(({ key, title }) => {
        const r: CheckResult | undefined = checks?.[key];
        const status = running ? 'pending' : (r?.status || 'pending');
        return (
          <div className={`sso-check ${status === 'pending' ? 'pending' : ''}`} key={key}>
            <span className={`sso-dot ${status}`} />
            <div>
              <div className="sso-check-title">{title}</div>
              <div className="sso-check-detail">
                {running ? 'Checking…' : (r?.detail || 'Not checked yet.')}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** "2h ago" — the sub-lines under a health pill are all relative. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The codebase's two-click confirm: the button relabels rather than opening a
 * dialog, and any other click resets it.
 */
export function useConfirm(): [number | string | null, (id: number | string | null) => void] {
  const [pending, setPending] = useState<number | string | null>(null);
  return [pending, setPending];
}
